/**
 * Notification bell — the entry point to the notification center.
 *
 * Renders in the Relay sidebar footer. Two shapes, mirroring ThemeToggle:
 *   - collapsed sidebar → a 9×9 icon button with a corner unread badge
 *   - expanded sidebar  → a full-width row ("Notifications" + unread count)
 *
 * Owns the open/closed state of the NotificationCenter dialog.
 */

import { useState } from "react";
import { Bell } from "lucide-react";

import { cn } from "@/lib/utils";
import { useUnreadNotificationCount } from "./api";
import { NotificationCenter } from "./NotificationCenter";

export function NotificationBell({
  collapsed = false,
  className,
  confirmNavigate,
}: {
  collapsed?: boolean;
  className?: string;
  /** Forwarded to NotificationCenter — see its Props for details. */
  confirmNavigate?: () => boolean;
}) {
  const [open, setOpen] = useState(false);
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const hasUnread = unreadCount > 0;
  const badgeLabel = unreadCount > 9 ? "9+" : String(unreadCount);
  const ariaLabel = hasUnread
    ? `Notifications, ${unreadCount} unread`
    : "Notifications";

  if (collapsed) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={ariaLabel}
          className={cn(
            "relative mx-auto flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
            className,
          )}
        >
          <Bell className="h-4 w-4" />
          {hasUnread && (
            <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground">
              {badgeLabel}
            </span>
          )}
        </button>
        <NotificationCenter open={open} onOpenChange={setOpen} confirmNavigate={confirmNavigate} />
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={ariaLabel}
        className={cn(
          "flex h-9 w-full items-center gap-2 rounded-lg border border-border bg-secondary/60 px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground",
          className,
        )}
      >
        <Bell className="h-4 w-4 shrink-0" />
        <span className="truncate">Notifications</span>
        {hasUnread && (
          <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold leading-none text-primary-foreground">
            {badgeLabel}
          </span>
        )}
      </button>
      <NotificationCenter open={open} onOpenChange={setOpen} confirmNavigate={confirmNavigate} />
    </>
  );
}
